import React, { useState } from "react";
import Sidebar from "../components/Sidebar";
import DashboardTopbar from "../components/DashboardTopbar";
import { ChevronLeft, Send } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

// — Sample data with quantities, customizations, and drinks —
const sampleOrders = [
  {
    id: 1,
    customer: "Sarah Thompson",
    items: [
      { name: "Pepperoni Pizza", quantity: 2, changes: "Extra cheese" },
      { name: "House Lemonade", quantity: 1, changes: "Less sugar" },
      { name: "Garlic Bread", quantity: 3, changes: "No butter" },
    ],
    notes: "Please call me when you arrive.",
    status: "new",
  },
  {
    id: 2,
    customer: "James Lee",
    items: [
      { name: "Vegan Burger", quantity: 1, changes: "No mayo, extra pickles" },
      { name: "Iced Coffee", quantity: 2, changes: "Almond milk" },
    ],
    notes: "Allergic to nuts, ring bell twice.",
    status: "getting_ready",
  },
];

const sampleSales = { series: [120, 150, 130, 170, 160, 180, 200] };
const samplePlatforms = {
  labels: ["DoorDash", "Uber Eats", "Grubhub"],
  values: [45, 30, 25],
};

const sampleMenuItems = [
  { id: 1, name: "Pepperoni Pizza", orders: 120 },
  { id: 2, name: "Garlic Bread", orders: 80 },
  { id: 3, name: "Cheeseburger", orders: 75 },
  { id: 4, name: "Caesar Salad", orders: 60 },
  { id: 5, name: "Chocolate Cake", orders: 50 },
];

const sampleRatings = { avg: 4.7, breakdown: [70, 20, 5, 3, 2] };

const sampleChats = [
  { id: 1, customer: "Sarah Thompson", last: "Thanks!" },
  { id: 2, customer: "James Lee", last: "Any updates?" },
];

const sampleMessages: Record<number, { from: string; text: string }[]> = {
  1: [
    { from: "Sarah Thompson", text: "Is my order ready?" },
    { from: "Support", text: "Checking now!" },
    { from: "Sarah Thompson", text: "Thanks!" },
  ],
  2: [
    { from: "James Lee", text: "When will my pizza be done?" },
    { from: "Support", text: "Almost there." },
  ],
};

// — Transform data for charts —
const salesData = sampleSales.series.map((value, i) => ({
  day: `Day ${i + 1}`,
  value,
}));
const platformData = samplePlatforms.labels.map((label, i) => ({
  name: label,
  value: samplePlatforms.values[i],
}));
const COLORS = ["#6366F1", "#10B981", "#F59E0B"];

const DashboardHome: React.FC = () => {
  const [selectedChat, setSelectedChat] = useState<number | null>(null);
  const [newMsg, setNewMsg] = useState<string>("");
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex flex-col flex-1">
        <DashboardTopbar
          logoUrl="/logo.png"
          name="FullPlateNow"
          address="123 Main St, Springfield"
        />
        <main className="p-6 space-y-6 flex-1 overflow-y-auto">
          {/* Summary Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                label: "Today’s Sales",
                value: `$${salesData[salesData.length - 1].value}`,
                accent: "border-indigo-500",
              },
              {
                label: "Active Orders",
                value: `${
                  sampleOrders.filter((o) => o.status === "new").length
                } Ongoing`,
                accent: "border-green-500",
              },
              {
                label: "Avg. Prep Time",
                value: "17 min",
                accent: "border-yellow-500",
              },
              {
                label: "Order Accuracy",
                value: "98.2%",
                accent: "border-pink-500",
              },
            ].map((card) => (
              <div
                key={card.label}
                className={`bg-white p-4 rounded-lg shadow border-l-4 ${card.accent} flex flex-col`}
              >
                <span className="text-gray-500 text-sm">{card.label}</span>
                <span className="mt-2 text-2xl font-bold">{card.value}</span>
              </div>
            ))}
          </div>

          {/* Main Widgets */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Live Order Feed */}
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-3">
                Live Order Feed
              </h3>
              <div className="space-y-2">
                {sampleOrders.map((order) => (
                  <div key={order.id}>
                    <button
                      onClick={() =>
                        setExpandedOrder(
                          expandedOrder === order.id ? null : order.id
                        )
                      }
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex justify-between items-center rounded"
                    >
                      <span className="font-medium text-gray-800">
                        {order.customer}
                      </span>
                      <span className="text-xs italic text-gray-600">
                        {order.items
                          .map((i) => `${i.quantity}× ${i.name}`)
                          .join(", ")}
                      </span>
                    </button>
                    {expandedOrder === order.id && (
                      <div className="bg-gray-50 px-6 py-4 border-l-4 border-gray-200 rounded-b-lg space-y-2">
                        <div>
                          <span className="font-semibold text-gray-700">
                            Items:
                          </span>
                          <ul className="list-disc list-inside mt-1 text-gray-700">
                            {order.items.map((i, idx) => (
                              <li key={idx}>
                                {i.quantity}× {i.name}
                                {i.changes && (
                                  <span className="italic text-gray-500">
                                    {" "}
                                    ({i.changes})
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <p className="text-gray-700">
                          <span className="font-semibold">Notes:</span>{" "}
                          {order.notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Charts & Chat Column */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Sales Line Chart */}
              <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Sales (Last 7 days)
                </h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={salesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#6366F1"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Platform Pie Chart */}
              <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Platform Breakdown
                </h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={platformData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={60}
                        label
                      >
                        {platformData.map((_, idx) => (
                          <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Legend verticalAlign="bottom" height={24} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Live Chat (light theme) */}
              {!selectedChat ? (
                <div className="sm:col-span-2 bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                  <h3 className="px-4 py-3 font-semibold text-gray-800 border-b">
                    Live Chat Inbox
                  </h3>
                  <div className="max-h-48 overflow-auto">
                    {sampleChats.map((thread) => (
                      <button
                        key={thread.id}
                        onClick={() => setSelectedChat(thread.id)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 flex justify-between"
                      >
                        <span className="font-medium text-gray-700">
                          {thread.customer}
                        </span>
                        <span className="text-xs text-gray-500">
                          {thread.last}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="sm:col-span-2 flex flex-col bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                  <div className="flex items-center px-4 py-3 border-b border-gray-200">
                    <button
                      onClick={() => setSelectedChat(null)}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <ChevronLeft size={20} className="text-gray-800" />
                    </button>
                    <h4 className="ml-3 font-medium text-gray-800">
                      {sampleChats.find((c) => c.id === selectedChat)?.customer}
                    </h4>
                  </div>
                  <div className="flex-1 overflow-auto p-4 space-y-3 bg-gray-50">
                    {sampleMessages[selectedChat!].map((msg, i) => (
                      <div
                        key={i}
                        className={`max-w-xs px-3 py-2 rounded-lg ${
                          msg.from === "Support"
                            ? "bg-blue-100 self-end"
                            : "bg-white"
                        }`}
                      >
                        <p className="text-sm text-gray-800">{msg.text}</p>
                        <span className="text-xs text-gray-500">
                          {msg.from}
                        </span>
                      </div>
                    ))}
                  </div>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setNewMsg("");
                    }}
                    className="flex items-center px-4 py-3 border-t border-gray-200 bg-white"
                  >
                    <input
                      type="text"
                      placeholder="Type a message…"
                      className="flex-1 px-4 py-2 border rounded-l-full focus:outline-none"
                      value={newMsg}
                      onChange={(e) => setNewMsg(e.target.value)}
                    />
                    <button
                      type="submit"
                      className="ml-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-r-full transition"
                    >
                      <Send size={20} />
                    </button>
                  </form>
                </div>
              )}

              {/* Top 5 Menu Items */}
              <div className="sm:col-span-2 bg-white p-4 rounded-lg shadow border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Top 5 Menu Items
                </h3>
                <ul className="space-y-2">
                  {sampleMenuItems.map((item) => (
                    <li
                      key={item.id}
                      className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded"
                    >
                      <span className="font-medium text-gray-700">
                        {item.name}
                      </span>
                      <span className="text-indigo-600 font-semibold">
                        {item.orders}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Customer Ratings */}
              <div className="sm:col-span-2 bg-white p-4 rounded-lg shadow border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Customer Ratings (30d)
                </h3>
                <div className="text-center mb-4">
                  <span className="text-4xl font-bold text-gray-800">
                    {sampleRatings.avg.toFixed(1)}★
                  </span>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  {[5, 4, 3, 2, 1].map((star, idx) => (
                    <div key={star} className="flex-1 px-1">
                      <span className="block text-left">{star}★</span>
                      <div className="h-2 bg-gray-200 rounded mt-1">
                        <div
                          className="h-full bg-yellow-400 rounded"
                          style={{ width: `${sampleRatings.breakdown[idx]}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardHome;
